import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, Modal,
  Dimensions, Animated, Platform, StatusBar, BackHandler,
  RefreshControl, Share, Alert,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width: W, height: H } = Dimensions.get('window');

// ─── AI SERVICE ───────────────────────────────────────────────────────────────
let aiService = null;
try { aiService = require('../../../services/aiService'); } catch {}

const askAnalyticsAI = async (prompt) => {
  const context = 'You are an expert Business Intelligence analyst for JobEng ERP. Provide concise, data-driven insights based on the financial and operational metrics provided.';
  if (aiService?.getAIResponse) {
    try { return await aiService.getAIResponse(prompt, context); } catch {}
  }
  try {
    const res = await fetch('https://api.jobeng.ai/api/ai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514', max_tokens: 600,
        system: context,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    const data = await res.json();
    return (data.text || data.content || data.choices?.[0]?.message?.content || '');
  } catch {}
  const fallbacks = {
    revenue: 'Revenue is tracking at £8.4M YTD (+18.2%). Q4 performance was exceptional with £2.4M in December. Key driver: Enterprise segment grew 22%. Recommendation: prioritise enterprise pipeline to maintain momentum.',
    forecast: 'Based on 9-month trend, Q1 2025 base-case projects £6.4M. Pipeline conversion at 24.8% suggests upside risk. Key risks: 3 enterprise renewals totalling £380k due Q1.',
    kpi: 'Overall KPI health score: 6/8 above target. Two underperformers: Employee NPS (78 vs target 80) and On-Time Delivery (87% vs target 90%). Recommend: HR engagement programme and PM resource review.',
    cost: 'Payroll at 49% of costs is within industry benchmarks (45-55%). Marketing spend at 12% is slightly above target. Consider rebalancing £40k from marketing to operations to improve delivery KPI.',
    default: 'Your ERP shows strong fundamentals: £8.4M revenue (+18.2%), 22.9% net margin, 92% CSAT. Strategic priorities: (1) Close 156 pipeline deals worth £4.8M (2) Address 2 below-target KPIs (3) Prepare Q1 board pack.',
  };
  const k = Object.keys(fallbacks).find(key => prompt.toLowerCase().includes(key)) || 'default';
  return fallbacks[k];
};

const BRAND = {
  blue: '#0e4e96', canary: '#fdb913', mint: '#19abb5',
  bg: '#f8fafc', surface: '#ffffff', text: '#0f172a', sub: '#475569',
  line: '#e2e8f0', success: '#10b981', warning: '#f59e0b',
  danger: '#ef4444', info: '#3b82f6', purple: '#8b5cf6', pink: '#ec4899',
};

// ─── DATA ─────────────────────────────────────────────────────────────────────
const MONTHLY = [
  { m: 'Jul', rev: 1.2, exp: 0.9, profit: 0.3, customers: 142, target: 1.3 },
  { m: 'Aug', rev: 1.4, exp: 1.0, profit: 0.4, customers: 156, target: 1.4 },
  { m: 'Sep', rev: 1.3, exp: 1.1, profit: 0.2, customers: 159, target: 1.5 },
  { m: 'Oct', rev: 1.6, exp: 1.2, profit: 0.4, customers: 167, target: 1.6 },
  { m: 'Nov', rev: 1.9, exp: 1.3, profit: 0.6, customers: 180, target: 1.7 },
  { m: 'Dec', rev: 2.4, exp: 1.7, profit: 0.7, customers: 198, target: 1.9 },
  { m: 'Jan', rev: 1.8, exp: 1.3, profit: 0.5, customers: 210, target: 2.0 },
  { m: 'Feb', rev: 2.1, exp: 1.4, profit: 0.7, customers: 225, target: 2.1 },
  { m: 'Mar', rev: 2.3, exp: 1.5, profit: 0.8, customers: 240, target: 2.2 },
];

const KPI_DATA = [
  { name: 'Revenue Growth', value: '+18.2%', target: '+15%', status: 'above', trend: [10,12,14,13,16,18], color: BRAND.success, icon: 'trending-up', desc: 'Year-over-year' },
  { name: 'Profit Margin', value: '22.9%', target: '20%', status: 'above', trend: [18,19,20,21,22,23], color: BRAND.blue, icon: 'cash', desc: 'Net margin' },
  { name: 'Customer CSAT', value: '92%', target: '90%', status: 'above', trend: [88,89,90,91,91,92], color: BRAND.mint, icon: 'star', desc: 'NPS: 72' },
  { name: 'Employee NPS', value: '78', target: '80', status: 'below', trend: [74,76,75,77,78,78], color: BRAND.warning, icon: 'account-group', desc: '245 surveyed' },
  { name: 'On-Time Delivery', value: '87%', target: '90%', status: 'below', trend: [82,84,85,86,87,87], color: BRAND.warning, icon: 'clock-check', desc: '34 projects' },
  { name: 'Inventory Turnover', value: '4.2x', target: '4.0x', status: 'above', trend: [3.6,3.7,3.9,4.0,4.1,4.2], color: BRAND.success, icon: 'warehouse', desc: 'Annual rate' },
  { name: 'Cash Conversion', value: '38 days', target: '45 days', status: 'above', trend: [48,45,43,41,40,38], color: BRAND.success, icon: 'bank', desc: 'Cash cycle' },
  { name: 'Gross Margin', value: '51.3%', target: '50%', status: 'above', trend: [47,48,49,50,51,51], color: BRAND.success, icon: 'percent', desc: 'Before overheads' },
];

const MODULE_PERF = [
  { name: 'CRM', icon: 'handshake', color: '#f97316', score: 85, deals: 156, revenue: '£384k', growth: '+18%' },
  { name: 'Finance', icon: 'cash-multiple', color: '#10b981', score: 92, transactions: 1247, revenue: '£2.45M', growth: '+12%' },
  { name: 'HR', icon: 'account-group', color: '#8b5cf6', score: 78, employees: 245, satisfaction: '87%', growth: '+5%' },
  { name: 'Inventory', icon: 'warehouse', color: '#19abb5', score: 71, items: 1247, value: '£890k', growth: '+7%' },
  { name: 'Projects', icon: 'clipboard-check', color: '#6366f1', score: 87, active: 34, onTime: '87%', growth: '+6%' },
  { name: 'Payroll', icon: 'currency-gbp', color: '#ec4899', score: 96, paid: 245, total: '£920k', growth: '+3%' },
];

const TOP_CUSTOMERS = [
  { name: 'Acme Corporation', revenue: '£285k', deals: 4, growth: '+22%', status: 'Enterprise' },
  { name: 'Beta Industries', revenue: '£198k', deals: 3, growth: '+15%', status: 'Mid-Market' },
  { name: 'Gamma Solutions', revenue: '£176k', deals: 5, growth: '+31%', status: 'SMB' },
  { name: 'Delta Tech Ltd', revenue: '£154k', deals: 2, growth: '+8%', status: 'Enterprise' },
  { name: 'TechCorp Inc', revenue: '£132k', deals: 3, growth: '+19%', status: 'Mid-Market' },
];

const COST_BREAKDOWN = [
  { name: 'Payroll', value: 920, pct: 49, color: BRAND.blue },
  { name: 'Operations', value: 340, pct: 18, color: BRAND.purple },
  { name: 'Marketing', value: 220, pct: 12, color: '#f97316' },
  { name: 'Tech & IT', value: 180, pct: 10, color: BRAND.mint },
  { name: 'Facilities', value: 140, pct: 7, color: BRAND.warning },
  { name: 'Other', value: 90, pct: 4, color: BRAND.sub },
];

const FORECAST = [
  { q: 'Q1 2025', low: 5.8, base: 6.4, high: 7.1, actual: null },
  { q: 'Q2 2025', low: 6.2, base: 7.0, high: 7.9, actual: null },
  { q: 'Q3 2025', low: 6.8, base: 7.6, high: 8.5, actual: null },
  { q: 'Q4 2025', low: 7.4, base: 8.4, high: 9.4, actual: null },
];

// ─── MINI SPARKLINE ───────────────────────────────────────────────────────────
const Sparkline = ({ data, color, width: w = 60, height: h = 24 }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => ({
    x: (i / (data.length - 1)) * w,
    y: h - ((v - min) / range) * h,
  }));
  return (
    <View style={{ width: w, height: h }}>
      {pts.slice(1).map((pt, i) => {
        const prev = pts[i];
        const dx = pt.x - prev.x;
        const dy = pt.y - prev.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        return (
          <View key={i} style={{ position: 'absolute', left: prev.x, top: prev.y, width: len, height: 2, backgroundColor: color, borderRadius: 1, transformOrigin: '0 50%', transform: [{ rotate: `${angle}deg` }] }} />
        );
      })}
      {pts.map((pt, i) => i === pts.length - 1 && (
        <View key={i} style={{ position: 'absolute', left: pt.x - 3, top: pt.y - 3, width: 6, height: 6, borderRadius: 3, backgroundColor: color }} />
      ))}
    </View>
  );
};

// ─── BAR CHART ────────────────────────────────────────────────────────────────
const BarChart = ({ data, height: H = 140, valueKey = 'rev', targetKey = 'target', label = 'm', color = BRAND.blue }) => {
  const maxV = Math.max(...data.map(d => Math.max(d[valueKey] || 0, d[targetKey] || 0)));
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 8, height: H + 30, paddingBottom: 4 }}>
        {data.map((d, i) => {
          const barH = Math.max(((d[valueKey] || 0) / maxV) * H, 4);
          const tarH = targetKey ? Math.max(((d[targetKey] || 0) / maxV) * H, 4) : 0;
          const beat = d[valueKey] >= (d[targetKey] || 0);
          return (
            <View key={i} style={{ alignItems: 'center', width: 48 }}>
              <Text style={{ color: BRAND.sub, fontSize: 9, marginBottom: 4 }}>{d[valueKey]?.toFixed ? `${d[valueKey].toFixed(1)}` : d[valueKey]}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 2 }}>
                {targetKey && <View style={{ width: 14, height: tarH, borderRadius: 3, backgroundColor: BRAND.line }} />}
                <View style={{ width: targetKey ? 14 : 28, height: barH, borderRadius: 3, backgroundColor: beat ? BRAND.success : color }} />
              </View>
              <Text style={{ color: BRAND.sub, fontSize: 9, marginTop: 5 }}>{d[label]}</Text>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
};

// ─── DONUT SEGMENT ────────────────────────────────────────────────────────────
const DonutChart = ({ data, size = 120 }) => {
  const total = data.reduce((s, d) => s + d.pct, 0);
  let offset = 0;
  return (
    <View style={{ width: size, height: size, position: 'relative' }}>
      {data.map((seg, i) => {
        const pct = seg.pct / total;
        const deg = pct * 360;
        const start = offset;
        offset += deg;
        const mid = (start + deg / 2) * (Math.PI / 180);
        const r = size / 2;
        const innerR = r * 0.6;
        return (
          <View key={i} style={{ position: 'absolute', width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
            <View style={{ width: r * 2 * pct, height: 8, backgroundColor: seg.color, borderRadius: 4, marginBottom: 2 }} />
          </View>
        );
      })}
      <View style={{ position: 'absolute', inset: 0, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ fontSize: 14, fontWeight: '800', color: BRAND.text }}>£{data.reduce((s, d) => s + d.value, 0).toLocaleString()}k</Text>
        <Text style={{ fontSize: 9, color: BRAND.sub }}>Total</Text>
      </View>
    </View>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const ERPAnalyticsScreen = ({ navigation }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('MTD');
  const [refreshing, setRefreshing] = useState(false);
  const [showKPIDetail, setShowKPIDetail] = useState(false);
  const [showForecastDetail, setShowForecastDetail] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [aiMessages, setAIMessages] = useState([{ role: 'assistant', text: "Hello! I'm your Analytics AI. Ask me about revenue trends, KPI performance, forecasts, or cost breakdown." }]);
  const [aiInput, setAIInput] = useState('');
  const [aiThinking, setAIThinking] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const aiScrollRef = useRef(null);

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showAIChat) { setShowAIChat(false); return true; }
      if (showKPIDetail || showForecastDetail) { setShowKPIDetail(false); setShowForecastDetail(false); return true; }
      return false;
    });
    return () => sub.remove();
  }, [showKPIDetail, showForecastDetail, showAIChat]);

  const sendAIMessage = async () => {
    const msg = aiInput.trim();
    if (!msg || aiThinking) return;
    setAIMessages(prev => [...prev, { role: 'user', text: msg }]);
    setAIInput('');
    setAIThinking(true);
    setTimeout(() => aiScrollRef.current?.scrollToEnd({ animated: true }), 100);
    const reply = await askAnalyticsAI(msg);
    setAIMessages(prev => [...prev, { role: 'assistant', text: reply }]);
    setAIThinking(false);
    setTimeout(() => aiScrollRef.current?.scrollToEnd({ animated: true }), 100);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await new Promise(r => setTimeout(r, 800));
    setRefreshing(false);
  };

  const handleExport = async () => {
    await Share.share({
      message: `JobEng ERP Analytics Report\n${'═'.repeat(30)}\nPeriod: ${period}\n\nRevenue: £8.4M (+18.2%)\nProfit Margin: 22.9%\nCustomers: 1,847\nEmployees: 245\n\nTop KPIs:\n${KPI_DATA.map(k => `${k.name}: ${k.value} (target: ${k.target})`).join('\n')}\n\nGenerated: ${new Date().toLocaleString()}`,
      title: 'ERP Analytics Report',
    });
  };

  const TABS = [
    { id: 'overview', label: 'Overview', icon: 'view-dashboard' },
    { id: 'revenue', label: 'Revenue', icon: 'trending-up' },
    { id: 'kpis', label: 'KPIs', icon: 'flag-checkered' },
    { id: 'modules', label: 'Modules', icon: 'apps' },
    { id: 'forecast', label: 'Forecast', icon: 'crystal-ball' },
    { id: 'costs', label: 'Costs', icon: 'cash-minus' },
  ];

  const renderOverview = () => (
    <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={BRAND.blue} />} showsVerticalScrollIndicator={false}>
      {/* Summary cards */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingLeft: 16, paddingVertical: 16 }}>
        {[
          { l: 'Total Revenue', v: '£8.4M', t: '+18.2%', c: BRAND.success, i: 'trending-up', bg: ['#10b981', '#059669'] },
          { l: 'Net Profit', v: '£1.93M', t: '+22.4%', c: '#fff', i: 'cash', bg: [BRAND.blue, '#163b6c'] },
          { l: 'Total Customers', v: '1,847', t: '+47 MTD', c: BRAND.success, i: 'account-group', bg: [BRAND.surface, BRAND.surface], border: true },
          { l: 'Employees', v: '245', t: '+12 hired', c: BRAND.purple, i: 'account-multiple', bg: [BRAND.surface, BRAND.surface], border: true },
          { l: 'Active Projects', v: '34', t: '87% on-time', c: BRAND.blue, i: 'clipboard-check', bg: [BRAND.surface, BRAND.surface], border: true },
          { l: 'Profit Margin', v: '22.9%', t: 'Above target', c: BRAND.success, i: 'percent', bg: [BRAND.surface, BRAND.surface], border: true },
        ].map((card, i) => (
          <LinearGradient key={i} colors={card.bg} style={[as.summaryCard, card.border && { borderWidth: 1, borderColor: BRAND.line }]}>
            <View style={[as.summaryCardIcon, { backgroundColor: card.border ? card.c + '18' : 'rgba(255,255,255,0.2)' }]}>
              <MaterialCommunityIcons name={card.i} size={20} color={card.border ? card.c : '#fff'} />
            </View>
            <Text style={[as.summaryValue, { color: card.border ? card.c : '#fff' }]}>{card.v}</Text>
            <Text style={[as.summaryLabel, { color: card.border ? BRAND.text : 'rgba(255,255,255,0.9)' }]}>{card.l}</Text>
            <Text style={[as.summaryTrend, { color: card.border ? BRAND.sub : 'rgba(255,255,255,0.7)' }]}>{card.t}</Text>
          </LinearGradient>
        ))}
        <View style={{ width: 16 }} />
      </ScrollView>

      {/* Revenue vs Expenses chart */}
      <View style={{ paddingHorizontal: 16, marginBottom: 20 }}>
        <View style={as.sectionHeader}>
          <Text style={as.sectionTitle}>Revenue vs Expenses</Text>
          <View style={as.legendRow}>
            <View style={as.legendItem}><View style={[as.legendDot, { backgroundColor: BRAND.success }]} /><Text style={as.legendText}>Revenue</Text></View>
            <View style={as.legendItem}><View style={[as.legendDot, { backgroundColor: BRAND.line }]} /><Text style={as.legendText}>Target</Text></View>
          </View>
        </View>
        <View style={[as.chartCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <BarChart data={MONTHLY} height={130} valueKey="rev" targetKey="target" label="m" color={BRAND.blue} />
        </View>
      </View>

      {/* KPI Summary */}
      <View style={{ paddingHorizontal: 16, marginBottom: 20 }}>
        <View style={as.sectionHeader}>
          <Text style={as.sectionTitle}>KPI Snapshot</Text>
          <TouchableOpacity onPress={() => setActiveTab('kpis')}><Text style={[as.viewAll, { color: BRAND.blue }]}>All KPIs →</Text></TouchableOpacity>
        </View>
        <View style={as.kpiGrid}>
          {KPI_DATA.slice(0, 6).map((kpi, i) => (
            <TouchableOpacity key={i} style={[as.kpiCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line, borderLeftColor: kpi.color, borderLeftWidth: 3 }]} onPress={() => { setSelectedKPI(kpi); setShowKPIDetail(true); }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <View style={{ flex: 1 }}>
                  <Text style={[as.kpiName, { color: BRAND.sub }]}>{kpi.name}</Text>
                  <Text style={[as.kpiValue, { color: kpi.color }]}>{kpi.value}</Text>
                  <Text style={[as.kpiTarget, { color: BRAND.sub }]}>Target: {kpi.target}</Text>
                </View>
                <View style={{ alignItems: 'flex-end', gap: 6 }}>
                  <MaterialCommunityIcons name={kpi.status === 'above' ? 'arrow-up-bold-circle' : 'arrow-down-bold-circle'} size={16} color={kpi.status === 'above' ? BRAND.success : BRAND.warning} />
                  <Sparkline data={kpi.trend} color={kpi.color} width={50} height={20} />
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Module performance */}
      <View style={{ paddingHorizontal: 16, marginBottom: 20 }}>
        <View style={as.sectionHeader}>
          <Text style={as.sectionTitle}>Module Performance</Text>
          <TouchableOpacity onPress={() => setActiveTab('modules')}><Text style={[as.viewAll, { color: BRAND.blue }]}>Detailed →</Text></TouchableOpacity>
        </View>
        {MODULE_PERF.slice(0, 4).map((m, i) => (
          <View key={i} style={[as.moduleRow, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
            <View style={[as.moduleIcon, { backgroundColor: m.color + '18' }]}><MaterialCommunityIcons name={m.icon} size={18} color={m.color} /></View>
            <Text style={[as.moduleName, { color: BRAND.text }]}>{m.name}</Text>
            <View style={as.scoreBarOuter}>
              <View style={[as.scoreBarInner, { width: `${m.score}%`, backgroundColor: m.score >= 90 ? BRAND.success : m.score >= 75 ? BRAND.blue : BRAND.warning }]} />
            </View>
            <Text style={[as.scoreText, { color: m.score >= 90 ? BRAND.success : m.score >= 75 ? BRAND.blue : BRAND.warning }]}>{m.score}</Text>
            <Text style={[as.moduleGrowth, { color: BRAND.success }]}>{m.growth}</Text>
          </View>
        ))}
      </View>

      {/* Top customers */}
      <View style={{ paddingHorizontal: 16, marginBottom: 30 }}>
        <Text style={as.sectionTitle}>Top Customers by Revenue</Text>
        <View style={[as.tableCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <View style={[as.tableHeader, { borderBottomColor: BRAND.line }]}>
            <Text style={[as.tableHead, { flex: 2 }]}>Customer</Text>
            <Text style={as.tableHead}>Revenue</Text>
            <Text style={as.tableHead}>Growth</Text>
          </View>
          {TOP_CUSTOMERS.map((c, i) => (
            <View key={i} style={[as.tableRow, { borderBottomColor: BRAND.line, borderBottomWidth: i < TOP_CUSTOMERS.length - 1 ? 1 : 0 }]}>
              <View style={{ flex: 2 }}>
                <Text style={[as.tableCell, { color: BRAND.text, fontWeight: '600' }]}>{c.name}</Text>
                <Text style={[as.tableCellSub, { color: BRAND.sub }]}>{c.status} · {c.deals} deals</Text>
              </View>
              <Text style={[as.tableCell, { color: BRAND.success, fontWeight: '700' }]}>{c.revenue}</Text>
              <Text style={[as.tableCell, { color: BRAND.success }]}>{c.growth}</Text>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  const renderRevenue = () => (
    <ScrollView showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={BRAND.blue} />}>
      <View style={{ padding: 16 }}>
        {/* Period selector */}
        <View style={[as.periodRow, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          {['MTD', 'QTD', 'YTD', '12M'].map(p => (
            <TouchableOpacity key={p} style={[as.periodBtn, period === p && { backgroundColor: BRAND.blue }]} onPress={() => setPeriod(p)}>
              <Text style={[as.periodText, { color: period === p ? '#fff' : BRAND.sub }]}>{p}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Revenue summary */}
        <View style={[as.revenueSummary, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <LinearGradient colors={[BRAND.blue, '#163b6c']} style={as.revenueSummaryGrad}>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, marginBottom: 4 }}>Total Revenue ({period})</Text>
            <Text style={{ color: '#fff', fontSize: 32, fontWeight: '900' }}>£8.4M</Text>
            <View style={{ flexDirection: 'row', gap: 16, marginTop: 8 }}>
              <View><Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>vs Last Period</Text><Text style={{ color: '#fdb913', fontWeight: '700', fontSize: 14 }}>+18.2%</Text></View>
              <View><Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>Expenses</Text><Text style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '700', fontSize: 14 }}>£6.47M</Text></View>
              <View><Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>Net Profit</Text><Text style={{ color: '#10b981', fontWeight: '700', fontSize: 14 }}>£1.93M</Text></View>
            </View>
          </LinearGradient>
        </View>

        {/* Monthly bar chart */}
        <View style={[as.chartCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <Text style={[as.chartTitle, { color: BRAND.text }]}>Monthly Revenue (£M)</Text>
          <BarChart data={MONTHLY} height={140} valueKey="rev" targetKey="target" label="m" color={BRAND.blue} />
          <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
            <View style={as.legendItem}><View style={[as.legendDot, { backgroundColor: BRAND.success }]} /><Text style={as.legendText}>Beat Target</Text></View>
            <View style={as.legendItem}><View style={[as.legendDot, { backgroundColor: BRAND.blue }]} /><Text style={as.legendText}>Below Target</Text></View>
            <View style={as.legendItem}><View style={[as.legendDot, { backgroundColor: BRAND.line }]} /><Text style={as.legendText}>Target</Text></View>
          </View>
        </View>

        {/* Revenue breakdown */}
        <View style={[as.chartCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <Text style={[as.chartTitle, { color: BRAND.text }]}>Revenue by Source</Text>
          {[
            { source: 'Enterprise Sales', value: '£3.2M', pct: 38, color: BRAND.blue },
            { source: 'Mid-Market', value: '£2.1M', pct: 25, color: BRAND.purple },
            { source: 'SMB', value: '£1.7M', pct: 20, color: '#f97316' },
            { source: 'Consulting', value: '£0.9M', pct: 11, color: BRAND.mint },
            { source: 'Renewals', value: '£0.5M', pct: 6, color: BRAND.success },
          ].map((s, i) => (
            <View key={i} style={{ marginBottom: 12 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}>
                <Text style={{ fontSize: 13, fontWeight: '600', color: BRAND.text }}>{s.source}</Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Text style={{ fontSize: 12, color: BRAND.sub }}>{s.pct}%</Text>
                  <Text style={{ fontSize: 13, fontWeight: '700', color: s.color }}>{s.value}</Text>
                </View>
              </View>
              <View style={{ height: 8, borderRadius: 4, backgroundColor: BRAND.line, overflow: 'hidden' }}>
                <View style={{ height: '100%', width: `${s.pct}%`, borderRadius: 4, backgroundColor: s.color }} />
              </View>
            </View>
          ))}
        </View>

        {/* Monthly detail table */}
        <View style={[as.tableCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <Text style={[as.chartTitle, { color: BRAND.text }]}>Monthly Summary</Text>
          <View style={[as.tableHeader, { borderBottomColor: BRAND.line }]}>
            <Text style={[as.tableHead, { flex: 1 }]}>Month</Text>
            <Text style={as.tableHead}>Revenue</Text>
            <Text style={as.tableHead}>Expenses</Text>
            <Text style={as.tableHead}>Profit</Text>
          </View>
          {MONTHLY.map((m, i) => (
            <View key={i} style={[as.tableRow, { borderBottomColor: BRAND.line, borderBottomWidth: i < MONTHLY.length - 1 ? 1 : 0 }]}>
              <Text style={[as.tableCell, { flex: 1, color: BRAND.text, fontWeight: '600' }]}>{m.m}</Text>
              <Text style={[as.tableCell, { color: BRAND.blue, fontWeight: '600' }]}>£{m.rev}M</Text>
              <Text style={[as.tableCell, { color: BRAND.danger }]}>£{m.exp}M</Text>
              <Text style={[as.tableCell, { color: BRAND.success, fontWeight: '700' }]}>£{m.profit}M</Text>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  const renderKPIs = () => (
    <ScrollView showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={BRAND.blue} />}>
      <View style={{ padding: 16 }}>
        <Text style={[as.sectionTitle, { marginBottom: 16 }]}>Key Performance Indicators</Text>
        {KPI_DATA.map((kpi, i) => (
          <TouchableOpacity key={i} style={[as.kpiDetailCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line, borderLeftColor: kpi.color, borderLeftWidth: 4 }]} onPress={() => { setSelectedKPI(kpi); setShowKPIDetail(true); }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <View style={[as.kpiDetailIcon, { backgroundColor: kpi.color + '18' }]}>
                <MaterialCommunityIcons name={kpi.icon} size={20} color={kpi.color} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[as.kpiDetailName, { color: BRAND.text }]}>{kpi.name}</Text>
                <Text style={[as.kpiDetailDesc, { color: BRAND.sub }]}>{kpi.desc}</Text>
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <Text style={[as.kpiDetailValue, { color: kpi.color }]}>{kpi.value}</Text>
                <View style={[as.kpiStatus, { backgroundColor: (kpi.status === 'above' ? BRAND.success : BRAND.warning) + '18' }]}>
                  <MaterialCommunityIcons name={kpi.status === 'above' ? 'arrow-up' : 'arrow-down'} size={10} color={kpi.status === 'above' ? BRAND.success : BRAND.warning} />
                  <Text style={[as.kpiStatusText, { color: kpi.status === 'above' ? BRAND.success : BRAND.warning }]}>{kpi.status === 'above' ? 'Above' : 'Below'}</Text>
                </View>
              </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 10, color: BRAND.sub, marginBottom: 4 }}>Progress to Target ({kpi.target})</Text>
                <View style={{ height: 6, borderRadius: 3, backgroundColor: BRAND.line, overflow: 'hidden' }}>
                  <View style={{ height: '100%', width: kpi.status === 'above' ? '100%' : '85%', borderRadius: 3, backgroundColor: kpi.status === 'above' ? BRAND.success : BRAND.warning }} />
                </View>
              </View>
              <Sparkline data={kpi.trend} color={kpi.color} width={64} height={28} />
            </View>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );

  const renderModules = () => (
    <ScrollView showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={BRAND.blue} />}>
      <View style={{ padding: 16 }}>
        <Text style={[as.sectionTitle, { marginBottom: 16 }]}>Module Performance Breakdown</Text>
        {MODULE_PERF.map((m, i) => (
          <View key={i} style={[as.modPerfCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <View style={[as.modPerfIcon, { backgroundColor: m.color + '18' }]}><MaterialCommunityIcons name={m.icon} size={24} color={m.color} /></View>
              <View style={{ flex: 1 }}>
                <Text style={[as.modPerfName, { color: BRAND.text }]}>{m.name}</Text>
                <Text style={[as.modPerfGrowth, { color: BRAND.success }]}>{m.growth} this month</Text>
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <Text style={[as.modPerfScore, { color: m.score >= 90 ? BRAND.success : m.score >= 75 ? BRAND.blue : BRAND.warning }]}>{m.score}</Text>
                <Text style={{ fontSize: 9, color: BRAND.sub }}>Health Score</Text>
              </View>
            </View>
            <View style={{ height: 8, borderRadius: 4, backgroundColor: BRAND.line, overflow: 'hidden', marginBottom: 12 }}>
              <View style={{ height: '100%', width: `${m.score}%`, borderRadius: 4, backgroundColor: m.score >= 90 ? BRAND.success : m.score >= 75 ? m.color : BRAND.warning }} />
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {Object.entries(m).filter(([k]) => !['name', 'icon', 'color', 'score', 'growth'].includes(k)).map(([key, val], ji) => (
                <View key={ji} style={[as.modStatChip, { borderColor: m.color + '30', backgroundColor: m.color + '08' }]}>
                  <Text style={[as.modStatKey, { color: BRAND.sub }]}>{key}</Text>
                  <Text style={[as.modStatVal, { color: m.color }]}>{val}</Text>
                </View>
              ))}
            </ScrollView>
          </View>
        ))}
      </View>
    </ScrollView>
  );

  const renderForecast = () => (
    <ScrollView showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={BRAND.blue} />}>
      <View style={{ padding: 16 }}>
        {/* AI forecast header */}
        <LinearGradient colors={['#6366f1', '#8b5cf6']} style={[as.forecastHeader, { borderRadius: 20, padding: 20, marginBottom: 16 }]}>
          <MaterialCommunityIcons name="crystal-ball" size={24} color="#fff" />
          <View style={{ marginTop: 10 }}>
            <Text style={{ color: '#fff', fontSize: 16, fontWeight: '800', marginBottom: 4 }}>AI Revenue Forecast 2025</Text>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>Based on historical trends, market data, and pipeline analysis</Text>
          </View>
          <View style={{ flexDirection: 'row', gap: 16, marginTop: 14 }}>
            <View><Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>Base Case</Text><Text style={{ color: '#fdb913', fontWeight: '800', fontSize: 18 }}>£29.4M</Text></View>
            <View><Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>Bull Case</Text><Text style={{ color: '#10b981', fontWeight: '800', fontSize: 18 }}>£32.9M</Text></View>
            <View><Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>Bear Case</Text><Text style={{ color: '#fc8181', fontWeight: '800', fontSize: 18 }}>£26.2M</Text></View>
          </View>
        </LinearGradient>

        {/* Quarterly forecast */}
        <Text style={[as.sectionTitle, { marginBottom: 12 }]}>Quarterly Projections</Text>
        {FORECAST.map((q, i) => (
          <View key={i} style={[as.forecastRow, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
            <View style={[as.forecastQuarter, { backgroundColor: BRAND.blue + '18' }]}>
              <Text style={{ color: BRAND.blue, fontWeight: '800', fontSize: 12 }}>{q.q}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text style={{ fontSize: 10, color: BRAND.sub }}>Bear: £{q.low}M</Text>
                <Text style={{ fontSize: 12, fontWeight: '800', color: BRAND.blue }}>Base: £{q.base}M</Text>
                <Text style={{ fontSize: 10, color: BRAND.sub }}>Bull: £{q.high}M</Text>
              </View>
              <View style={{ height: 10, borderRadius: 5, backgroundColor: BRAND.line, overflow: 'hidden' }}>
                <View style={{ height: '100%', borderRadius: 5, backgroundColor: BRAND.success + '30', position: 'absolute', left: `${((q.low - 5) / 5) * 100}%`, right: `${((9.5 - q.high) / 5) * 100}%` }} />
                <View style={{ height: '100%', width: 3, borderRadius: 5, backgroundColor: BRAND.blue, position: 'absolute', left: `${((q.base - 5) / 5) * 100}%` }} />
              </View>
            </View>
          </View>
        ))}

        {/* Assumptions */}
        <View style={[as.chartCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <Text style={[as.chartTitle, { color: BRAND.text }]}>Forecast Assumptions</Text>
          {[
            { factor: 'Market Growth Rate', value: '+8% YoY', conf: 'High' },
            { factor: 'Pipeline Conversion', value: '24.8% → 28%', conf: 'Medium' },
            { factor: 'New Customer Acq.', value: +60, conf: 'Medium' },
            { factor: 'Average Deal Size', value: '£32k (+12%)', conf: 'High' },
            { factor: 'Churn Rate', value: '<5% (stable)', conf: 'High' },
          ].map((a, i) => (
            <View key={i} style={[as.assumptionRow, { borderBottomColor: BRAND.line, borderBottomWidth: i < 4 ? 1 : 0 }]}>
              <Text style={{ flex: 1, fontSize: 13, color: BRAND.text }}>{a.factor}</Text>
              <Text style={{ fontSize: 13, fontWeight: '600', color: BRAND.blue, marginRight: 12 }}>{a.value}</Text>
              <View style={[as.confBadge, { backgroundColor: a.conf === 'High' ? BRAND.success + '18' : BRAND.warning + '18' }]}>
                <Text style={{ fontSize: 10, fontWeight: '700', color: a.conf === 'High' ? BRAND.success : BRAND.warning }}>{a.conf}</Text>
              </View>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );

  const renderCosts = () => (
    <ScrollView showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={BRAND.blue} />}>
      <View style={{ padding: 16 }}>
        <View style={[as.revenueSummary, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <LinearGradient colors={[BRAND.danger + 'dd', BRAND.danger + 'aa']} style={as.revenueSummaryGrad}>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, marginBottom: 4 }}>Total Expenditure (YTD)</Text>
            <Text style={{ color: '#fff', fontSize: 30, fontWeight: '900' }}>£6.47M</Text>
            <View style={{ flexDirection: 'row', gap: 16, marginTop: 8 }}>
              <View><Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>Largest Cost</Text><Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>Payroll (49%)</Text></View>
              <View><Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>vs Last Year</Text><Text style={{ color: '#fdb913', fontWeight: '700', fontSize: 13 }}>+8.4%</Text></View>
            </View>
          </LinearGradient>
        </View>

        <Text style={[as.sectionTitle, { marginBottom: 12 }]}>Cost Breakdown</Text>
        {COST_BREAKDOWN.map((c, i) => (
          <View key={i} style={{ marginBottom: 14 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: c.color }} />
                <Text style={{ fontSize: 13, fontWeight: '600', color: BRAND.text }}>{c.name}</Text>
              </View>
              <View style={{ flexDirection: 'row', gap: 10 }}>
                <Text style={{ fontSize: 12, color: BRAND.sub }}>{c.pct}%</Text>
                <Text style={{ fontSize: 13, fontWeight: '700', color: c.color }}>£{c.value}k</Text>
              </View>
            </View>
            <View style={{ height: 8, borderRadius: 4, backgroundColor: BRAND.line, overflow: 'hidden' }}>
              <View style={{ height: '100%', width: `${c.pct}%`, borderRadius: 4, backgroundColor: c.color }} />
            </View>
          </View>
        ))}

        <View style={[as.chartCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <Text style={[as.chartTitle, { color: BRAND.text }]}>Monthly Expenses</Text>
          <BarChart data={MONTHLY} height={120} valueKey="exp" targetKey={null} label="m" color={BRAND.danger} />
        </View>
      </View>
    </ScrollView>
  );

  const renderContent = () => {
    if (activeTab === 'overview') return renderOverview();
    if (activeTab === 'revenue') return renderRevenue();
    if (activeTab === 'kpis') return renderKPIs();
    if (activeTab === 'modules') return renderModules();
    if (activeTab === 'forecast') return renderForecast();
    if (activeTab === 'costs') return renderCosts();
  };

  return (
    <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
      <StatusBar barStyle="light-content" backgroundColor={BRAND.blue} />
      <LinearGradient colors={[BRAND.blue, '#163b6c']} style={as.header}>
        <View style={as.headerRow}>
          <TouchableOpacity style={as.backBtn} onPress={() => navigation?.goBack()}>
            <MaterialCommunityIcons name="arrow-left" size={22} color="#fff" />
          </TouchableOpacity>
          <View style={{ flex: 1 }}>
            <Text style={as.headerTitle}>Analytics & BI</Text>
            <Text style={as.headerSub}>Business Intelligence Dashboard</Text>
          </View>
          <TouchableOpacity style={as.headerBtn} onPress={() => setShowAIChat(true)}>
            <MaterialCommunityIcons name="robot" size={20} color="#fff" />
          </TouchableOpacity>
          <TouchableOpacity style={as.headerBtn} onPress={handleExport}>
            <MaterialCommunityIcons name="export" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8, marginBottom: -6 }}>
          {TABS.map(tab => (
            <TouchableOpacity key={tab.id} style={[as.tab, activeTab === tab.id && as.tabActive]} onPress={() => setActiveTab(tab.id)}>
              <MaterialCommunityIcons name={tab.icon} size={13} color={activeTab === tab.id ? BRAND.blue : 'rgba(255,255,255,0.75)'} />
              <Text style={[as.tabText, activeTab === tab.id && { color: BRAND.blue }]}>{tab.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </LinearGradient>

      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>{renderContent()}</Animated.View>


      {/* AI CHAT MODAL */}
      <Modal visible={showAIChat} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAIChat(false)}>
        <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
          <LinearGradient colors={['#6366f1', '#8b5cf6']} style={{ flexDirection: 'row', alignItems: 'center', padding: 16, paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) + 16 : 16, gap: 12 }}>
            <TouchableOpacity style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' }} onPress={() => setShowAIChat(false)}>
              <MaterialCommunityIcons name="close" size={22} color="#fff" />
            </TouchableOpacity>
            <View style={{ width: 36, height: 36, borderRadius: 11, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' }}>
              <MaterialCommunityIcons name="robot" size={20} color="#fff" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ color: '#fff', fontSize: 15, fontWeight: '800' }}>Analytics AI</Text>
              <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11 }}>Ask about your business data</Text>
            </View>
            <TouchableOpacity onPress={() => setAIMessages([{ role: 'assistant', text: "Hello! I'm your Analytics AI. Ask me about revenue trends, KPI performance, forecasts, or cost breakdown." }])}>
              <MaterialCommunityIcons name="refresh" size={18} color="rgba(255,255,255,0.8)" />
            </TouchableOpacity>
          </LinearGradient>
          <ScrollView ref={aiScrollRef} style={{ flex: 1, padding: 16 }} contentContainerStyle={{ paddingBottom: 20 }} showsVerticalScrollIndicator={false}>
            {[
              'Revenue this month vs target?', 'Which KPIs are below target?',
              'Forecast for Q1 2025?', 'Biggest cost driver?',
              'Top performing module?', 'How to improve profit margin?',
            ].length > 0 && aiMessages.length <= 1 && (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {['Revenue vs target?', 'KPIs below target?', 'Q1 2025 forecast?', 'Biggest cost driver?', 'Top module?', 'Improve margin?'].map((s, i) => (
                  <TouchableOpacity key={i} style={{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 16, backgroundColor: '#6366f1' + '15', borderWidth: 1, borderColor: '#6366f1' + '30' }} onPress={() => { setAIInput(s); }}>
                    <Text style={{ color: '#6366f1', fontSize: 12, fontWeight: '600' }}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
            {aiMessages.map((msg, i) => (
              <View key={i} style={{ marginBottom: 12, alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                {msg.role === 'assistant' && (
                  <View style={{ width: 24, height: 24, borderRadius: 8, backgroundColor: '#6366f1', justifyContent: 'center', alignItems: 'center', marginBottom: 4 }}>
                    <MaterialCommunityIcons name="robot" size={13} color="#fff" />
                  </View>
                )}
                <View style={{ maxWidth: '85%', padding: 12, borderRadius: msg.role === 'user' ? 16 : 14, backgroundColor: msg.role === 'user' ? '#6366f1' : BRAND.surface, borderWidth: msg.role === 'assistant' ? 1 : 0, borderColor: BRAND.line }}>
                  <Text style={{ color: msg.role === 'user' ? '#fff' : BRAND.text, fontSize: 13, lineHeight: 19 }}>{msg.text}</Text>
                </View>
              </View>
            ))}
            {aiThinking && (
              <View style={{ alignItems: 'flex-start', marginBottom: 12 }}>
                <View style={{ padding: 12, borderRadius: 14, backgroundColor: BRAND.surface, borderWidth: 1, borderColor: BRAND.line }}>
                  <Text style={{ color: BRAND.sub, fontSize: 13 }}>Analysing data…</Text>
                </View>
              </View>
            )}
          </ScrollView>
          <View style={{ flexDirection: 'row', gap: 10, padding: 12, backgroundColor: BRAND.surface, borderTopWidth: 1, borderTopColor: BRAND.line }}>
            <TextInput value={aiInput} onChangeText={setAIInput} placeholder="Ask about your analytics…" placeholderTextColor={BRAND.sub} style={{ flex: 1, fontSize: 14, color: BRAND.text, backgroundColor: BRAND.bg, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 10, borderWidth: 1, borderColor: BRAND.line }} onSubmitEditing={sendAIMessage} returnKeyType="send" />
            <TouchableOpacity style={{ width: 44, height: 44, borderRadius: 14, backgroundColor: '#6366f1', justifyContent: 'center', alignItems: 'center', opacity: aiInput.trim() ? 1 : 0.5 }} onPress={sendAIMessage} disabled={!aiInput.trim() || aiThinking}>
              <MaterialCommunityIcons name="send" size={18} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* KPI Detail Modal */}
      <Modal visible={showKPIDetail} animationType="slide" presentationStyle="formSheet" onRequestClose={() => setShowKPIDetail(false)}>
        <View style={{ flex: 1, backgroundColor: BRAND.bg, padding: 20 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <Text style={{ fontSize: 18, fontWeight: '800', color: BRAND.text }}>{selectedKPI?.name}</Text>
            <TouchableOpacity onPress={() => setShowKPIDetail(false)}><MaterialCommunityIcons name="close" size={22} color={BRAND.sub} /></TouchableOpacity>
          </View>
          {selectedKPI && (
            <>
              <View style={{ alignItems: 'center', marginBottom: 24 }}>
                <Text style={{ fontSize: 52, fontWeight: '900', color: selectedKPI.color }}>{selectedKPI.value}</Text>
                <Text style={{ fontSize: 14, color: BRAND.sub }}>Target: {selectedKPI.target}</Text>
                <View style={[as.kpiStatus, { backgroundColor: (selectedKPI.status === 'above' ? BRAND.success : BRAND.warning) + '18', marginTop: 8 }]}>
                  <Text style={[as.kpiStatusText, { color: selectedKPI.status === 'above' ? BRAND.success : BRAND.warning, fontSize: 14 }]}>{selectedKPI.status === 'above' ? '✓ Above Target' : '⚠ Below Target'}</Text>
                </View>
              </View>
              <Text style={{ fontSize: 14, fontWeight: '700', color: BRAND.text, marginBottom: 10 }}>6-Month Trend</Text>
              <Sparkline data={selectedKPI.trend} color={selectedKPI.color} width={W - 72} height={60} />
            </>
          )}
        </View>
      </Modal>
    </View>
  );
};

const as = StyleSheet.create({
  header: { paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) : 0, paddingHorizontal: 16, paddingBottom: 0 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12 },
  backBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  headerTitle: { color: '#fff', fontSize: 16, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 1 },
  headerBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  tab: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 18, marginRight: 6, marginBottom: 8 },
  tabActive: { backgroundColor: '#fff' },
  tabText: { color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: '600' },
  sectionTitle: { fontSize: 15, fontWeight: '700', color: BRAND.text },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  viewAll: { fontSize: 12, fontWeight: '600' },
  summaryCard: { width: 150, borderRadius: 18, padding: 16, marginRight: 10, elevation: 3, shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.1, shadowRadius: 8 },
  summaryCardIcon: { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center', marginBottom: 10 },
  summaryValue: { fontSize: 22, fontWeight: '900', marginBottom: 2 },
  summaryLabel: { fontSize: 11, fontWeight: '600', marginBottom: 2 },
  summaryTrend: { fontSize: 10 },
  legendRow: { flexDirection: 'row', gap: 12 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendDot: { width: 8, height: 8, borderRadius: 4 },
  legendText: { fontSize: 10, color: BRAND.sub },
  chartCard: { borderRadius: 16, padding: 16, borderWidth: 1, marginBottom: 16, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 5 },
  chartTitle: { fontSize: 14, fontWeight: '700', marginBottom: 14 },
  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  kpiCard: { width: (W - 52) / 2, borderRadius: 14, padding: 13, borderWidth: 1, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3 },
  kpiName: { fontSize: 10, fontWeight: '600', marginBottom: 4 },
  kpiValue: { fontSize: 18, fontWeight: '900', marginBottom: 2 },
  kpiTarget: { fontSize: 9 },
  kpiStatus: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 7, paddingVertical: 3, borderRadius: 8 },
  kpiStatusText: { fontSize: 10, fontWeight: '700' },
  moduleRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, borderWidth: 1, marginBottom: 8 },
  moduleIcon: { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  moduleName: { width: 80, fontSize: 12, fontWeight: '700' },
  scoreBarOuter: { flex: 1, height: 6, borderRadius: 3, backgroundColor: BRAND.line, overflow: 'hidden' },
  scoreBarInner: { height: '100%', borderRadius: 3 },
  scoreText: { width: 28, fontSize: 12, fontWeight: '800', textAlign: 'right' },
  moduleGrowth: { width: 36, fontSize: 11, fontWeight: '600', textAlign: 'right' },
  tableCard: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', marginBottom: 16, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  tableHeader: { flexDirection: 'row', padding: 12, borderBottomWidth: 1, backgroundColor: BRAND.bg },
  tableHead: { width: 90, fontSize: 11, fontWeight: '700', color: BRAND.sub, textAlign: 'right' },
  tableRow: { flexDirection: 'row', alignItems: 'center', padding: 12 },
  tableCell: { width: 90, fontSize: 12, textAlign: 'right' },
  tableCellSub: { fontSize: 10, marginTop: 2 },
  periodRow: { flexDirection: 'row', borderRadius: 12, borderWidth: 1, overflow: 'hidden', marginBottom: 16 },
  periodBtn: { flex: 1, paddingVertical: 10, alignItems: 'center', borderRadius: 10 },
  periodText: { fontSize: 13, fontWeight: '700' },
  revenueSummary: { borderRadius: 20, overflow: 'hidden', marginBottom: 16, borderWidth: 1 },
  revenueSummaryGrad: { padding: 20 },
  kpiDetailCard: { borderRadius: 16, padding: 14, borderWidth: 1, marginBottom: 12, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  kpiDetailIcon: { width: 44, height: 44, borderRadius: 13, justifyContent: 'center', alignItems: 'center' },
  kpiDetailName: { fontSize: 14, fontWeight: '700', marginBottom: 2 },
  kpiDetailDesc: { fontSize: 11 },
  kpiDetailValue: { fontSize: 20, fontWeight: '900', marginBottom: 4 },
  modPerfCard: { borderRadius: 16, padding: 16, borderWidth: 1, marginBottom: 12, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  modPerfIcon: { width: 48, height: 48, borderRadius: 14, justifyContent: 'center', alignItems: 'center' },
  modPerfName: { fontSize: 15, fontWeight: '700', marginBottom: 3 },
  modPerfGrowth: { fontSize: 12, fontWeight: '600' },
  modPerfScore: { fontSize: 28, fontWeight: '900' },
  modStatChip: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, borderWidth: 1, marginRight: 8, alignItems: 'center' },
  modStatKey: { fontSize: 9, textTransform: 'uppercase', fontWeight: '600', marginBottom: 2 },
  modStatVal: { fontSize: 13, fontWeight: '800' },
  forecastHeader: {},
  forecastRow: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1, marginBottom: 10 },
  forecastQuarter: { width: 64, height: 40, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  assumptionRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12 },
  confBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
});

export default ERPAnalyticsScreen;
